
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { formatDate, formatDateTime, WindowType } from '../../../util';
import { Board, Card, Ticket } from '../../backend/backendapi';
import { KanbanState, SelectedItemDto } from '../kanban.model';
import DetailsModal from '../modal/DetailsModal';
import ActivityStore from '../../../stores/ActivityStore';
import LinkedActivityCheckableIcon from './KanbanCard/LinkedActivityCheckableIcon';
import { isBoardNotInSelectedAccount } from '../kanban.util';

declare var window: WindowType;

@Component({
  name: 'KanbanCard',
  components: {
    DetailsModal,
    LinkedActivityCheckableIcon,
  },
})
export default class KanbanCard extends Vue {
  @Prop({ required: true })
  state!: KanbanState;
  @Prop({ required: true })
  board!: Board;
  @Prop({ required: true })
  flow!: Ticket;
  @Prop({ required: true })
  card!: Card;

  readonly activityStore = new ActivityStore();

  get completedTaskCount(): number {
    if (!this.card.taskItems)
      return 0;
    return this.card.taskItems.filter(e => e.status === 'DONE').length;
  }

  get createdDate(): string {
    if (!this.card.createdOn)
      return '';
    if (window.application.kanban.kanbanshowtime) {
      return formatDateTime(this.card.createdOn);
    } else {
      return formatDate(this.card.createdOn);
    }
  }

  get desc(): string {
    return this.card.description?.split('\n', 2)[0] ?? '';
  }

  get dueDateShort(): string {
    if (!this.card.dueDate)
      return '';
    return formatDate(this.card.dueDate);
  }

  get dueDate(): string {
    if (!this.card.dueDate)
      return '';
    if (window.application.kanban.kanbanshowtime) {
      return formatDateTime(this.card.dueDate);
    } else {
      return formatDate(this.card.dueDate);
    }
  }

  get dueDateColor(): string {
    if (!this.card.dueDate)
      return '#000';
    const relativeTo = this.card.archived && this.card.archivedOn ? this.card.archivedOn.getTime() : Date.now();
    const diff = this.card.dueDate.getTime() - relativeTo;
    if (diff > 7 * 24 * 60 * 60 * 1000)
      return 'var(--success)';
    else if (diff > 0)
      return 'var(--warning)';
    else
      return 'var(--danger)';
  }

  get isBoardNotInSelectedAccount(): boolean {
    return isBoardNotInSelectedAccount(this.board, this.state.accounts);
  }

  get isShared(): boolean {
    return this.state.shareCardInfos.filter(e => e.cardId == this.card.id && e.accountIds.length > 0).length > 0;
  }

  get lastComment(): string {
    if (!this.card.comments?.length)
      return '';
    return this.card.comments[this.card.comments.length - 1].text ?? '';
  }

  get lastCommentUser(): string {
    if (!this.card.comments?.length)
      return '';
    let user = this.card.comments[this.card.comments.length - 1].aspNetUser;
    if (!user)
      return '';
    return user.firstName + ' ' + user.lastName;
  }

  get lastCommentDate(): string {
    if (!this.card.comments?.length)
      return '';
    let date = this.card.comments[this.card.comments.length - 1].createdOn;
    if (!date)
      return '';
    return date.toLocaleDateString() + (window.application.kanban.kanbanshowtime ? ' ' + date.toLocaleTimeString() : '');
  }


  get responsible(): string {
    if (!this.card.responsibles?.length)
      return '';
    let resps = this.card.responsibles
        .map(e => e.user)
        .sort((l, r) => (l?.lastName + ' ' + l?.firstName).toLocaleLowerCase().localeCompare((r?.lastName + ' ' + r?.firstName).toLocaleLowerCase()));
    if (resps.length == 1) {
      return resps[0]?.firstName + ' ' + resps[0]?.lastName;
    }
    return resps[0]?.firstName + ' ' + resps[0]?.lastName + ', +' + (resps.length - 1);
  }

  get responsibleFull(): string {
    if (!this.card.responsibles?.length)
      return ' ';
    let resps = this.card.responsibles.map(e => e.user);
    if (resps.length == 1) {
      return resps[0]?.firstName + ' ' + resps[0]?.lastName;
    }
    return resps.map(e => e?.firstName + ' ' + e?.lastName).reduce((l, r) => l + ', ' + r);
  }

  get visibleOnFilter(): boolean {
    return this.state.filter(this.card);
  }

  mounted(): void {
    this.activityStore.loadIfAbsent();
  }

  isActivityClosed(id: number): boolean {
    return !this.isBoardNotInSelectedAccount
        && !this.activityStore.loading
        && !this.activityStore.activities.find(e => e.id === id);
  }

  @Watch('state.selectedViaLog', { deep: true })
  private onSelectedLogChanged(log: SelectedItemDto | null): void {
    this.$nextTick(() => {
      if (log?.cardId !== this.card.id) {
        this.$el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
      }
    });
  }
}
